import type { ChatMessage, ChatTranscriptItem, LiveSubagent, ToolCallData } from '../../api/types'

// The chat feed folds consecutive agent steps (tool calls and the reasoning
// between them) into one collapsible group; assistant text and user messages
// sit between groups. Same rule as meow-coding's transcript.

export type FeedStep =
  | { kind: 'tool'; tool: ToolCallData }
  | { kind: 'thought'; id: string; text: string }

export type FeedBlock =
  | { kind: 'message'; message: ChatMessage }
  | { kind: 'steps'; id: string; steps: FeedStep[] }

export type StepState = 'running' | 'done' | 'error'

export function toolState(tool: ToolCallData): StepState {
  if (tool.error || tool.permission === 'denied') return 'error'
  return tool.output !== undefined ? 'done' : 'running'
}

/**
 * Live state for one subagent, keyed by task id: the identity the server sends for a
 * run already in flight (`ChatState.subagents`) plus what the `subagent-event`s add
 * while this client is watching.
 */
export interface SubagentLiveInfo extends Omit<LiveSubagent, 'state'> {
  state: 'running' | 'completed' | 'cancelled' | 'error'
  text: string
  reasoning: string
  toolCalls?: string[]
  result?: string
}

/** The task id a card names: result metadata, then an input `task_id` resume, then its `<task id="…">` output. */
function taskIdOf(tool: ToolCallData): string | undefined {
  const meta = (tool.metadata as Record<string, unknown> | undefined)?.taskId
  if (typeof meta === 'string' && meta) return meta
  const input = (tool.input as Record<string, unknown> | undefined)?.task_id
  if (typeof input === 'string' && input) return input
  return tool.output?.match(/<task\s+id="([^"]+)"/)?.[1]
}

/**
 * Joins every `task` call in one step group to the live subagent it started.
 *
 * Identity first: a call id belongs to exactly one tool call, so it wins outright
 * whatever the card's state. The task id (result metadata / input `task_id` / the
 * `<task id="…">` output) is a weaker join and only for a card that is still running
 * or a background dispatch (whose card is already "done" the moment the subagent is
 * handed off). Every live entry is claimed at most once, so a card that has finished
 * can never borrow the tokens of the run started next to it: the client can join a
 * run mid-flight (an old card has no entry of its own), a resumed subagent reuses
 * its task id, and a stopped subagent leaves its entry behind — matching by task id
 * alone made old cards look like they had restarted, and hid the new run's card.
 */
export function matchSubagents(
  tools: ToolCallData[],
  subagents?: Record<string, SubagentLiveInfo>
): Record<string, SubagentLiveInfo> {
  const out: Record<string, SubagentLiveInfo> = {}
  if (!subagents) return out
  const live = Object.values(subagents)
  const claimed = new Set<string>()

  for (const tool of tools) {
    const hit = live.find(s => s.callId !== undefined && s.callId === tool.id && !claimed.has(s.taskId))
    if (hit) {
      out[tool.id] = hit
      claimed.add(hit.taskId)
    }
  }

  for (const tool of tools) {
    if (out[tool.id]) continue
    const taskId = taskIdOf(tool)
    const hit = taskId ? subagents[taskId] : undefined
    if (!hit || claimed.has(hit.taskId)) continue
    const background = hit.background === true || (tool.metadata as Record<string, unknown> | undefined)?.background === true
    if (toolState(tool) !== 'running' && !background) continue
    out[tool.id] = hit
    claimed.add(hit.taskId)
  }

  // One running card and one unclaimed running entry can only be each other.
  const cards = tools.filter(t => !out[t.id] && toolState(t) === 'running')
  const free = live.filter(s => !claimed.has(s.taskId) && s.state === 'running')
  if (cards.length === 1 && free.length === 1) out[cards[0].id] = free[0]
  return out
}

/**
 * Live runs the transcript has no card for.
 *
 * A tool call is persisted only once it finishes, so a chat opened (or reloaded)
 * while a subagent runs has no `task` row to hang the run on — and neither has a
 * client that joined while the model was still writing the call. Without a card of
 * their own, the run's text, tools and reasoning stream into nothing until the turn
 * ends and the transcript is refetched. Only running entries: a finished run's
 * result travels with the card the refetch brings.
 */
export function orphanSubagents(
  items: ChatTranscriptItem[],
  subagents?: Record<string, SubagentLiveInfo>
): SubagentLiveInfo[] {
  if (!subagents) return []
  const tools = items.flatMap(i => (i.kind === 'tool' && i.tool.tool === 'task' ? [i.tool] : []))
  const claimed = new Set(Object.values(matchSubagents(tools, subagents)).map(s => s.taskId))
  return Object.values(subagents).filter(s => s.state === 'running' && !claimed.has(s.taskId))
}

/**
 * Groups a transcript into feed blocks. An assistant message's reasoning joins
 * the current step group as a "thought"; its text (if any) closes the group.
 * Empty assistant messages are dropped unless `streamingId` names them, so the
 * streaming caret still has somewhere to render.
 */
export function groupFeed(items: ChatTranscriptItem[], streamingId?: string | null): FeedBlock[] {
  const out: FeedBlock[] = []
  let open: Extract<FeedBlock, { kind: 'steps' }> | null = null
  const push = (step: FeedStep) => {
    if (!open) {
      open = { kind: 'steps', id: step.kind === 'tool' ? step.tool.id : step.id, steps: [] }
      out.push(open)
    }
    open.steps.push(step)
  }
  for (const item of items) {
    if (item.kind === 'tool') { push({ kind: 'tool', tool: item.tool }); continue }
    const m = item.message
    if (m.role !== 'assistant') { open = null; out.push(item); continue }
    if (m.reasoning?.trim()) push({ kind: 'thought', id: `${m.id}:thought`, text: m.reasoning })
    if (m.text || (m.id === streamingId && !m.reasoning?.trim())) {
      open = null
      out.push({ kind: 'message', message: m.reasoning ? { ...m, reasoning: undefined } : m })
    }
  }
  return out
}

export type StepCategory = 'command' | 'read' | 'search' | 'edit' | 'subagent' | 'tool'

const CATEGORY: Record<string, StepCategory> = {
  bash: 'command', monitor: 'command',
  read: 'read',
  glob: 'search', grep: 'search', websearch: 'search',
  write: 'edit', edit: 'edit', 'apply-patch': 'edit',
  task: 'subagent'
}

export function stepCategory(tool: string): StepCategory {
  return CATEGORY[tool] ?? (/(_search|_find|_list)/.test(tool) ? 'search' : 'tool')
}

function str(input: Record<string, unknown> | undefined, keys: string[]): string {
  for (const k of keys) {
    const v = input?.[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

export function baseName(path: string): string {
  const parts = path.split(/[\\/]/).filter(Boolean)
  return parts.length > 0 ? parts[parts.length - 1] : path
}

// Agents prefix almost every command with `cd <project> &&`, which pushes the
// part that matters off the end of a one-line row.
export function stripCdPrefix(command: string): string {
  return command.replace(/^cd\s+("[^"]*"|'[^']*'|\S+)\s*&&\s*/, '')
}

function filePaths(tool: ToolCallData): string[] {
  if (tool.tool === 'apply-patch') {
    const patch = str(tool.input, ['patch'])
    return patch ? [...patch.matchAll(/^\*\*\* (?:Update|Add|Delete) File: (.+)$/gm)].map(m => m[1].trim()) : []
  }
  const p = str(tool.input, ['file_path', 'path'])
  return p ? [p] : []
}

export interface StepSummary {
  category: StepCategory
  count: number
  /** Set when the part names one thing: the only file read/edited, or the only other tool used. */
  name?: string
}

/**
 * The group's one-line summary, by category in first-seen order ("Read 2 files, edited a.ts, ran a
 * command"). Files are counted once however often they were touched.
 */
export function summarizeSteps(steps: FeedStep[]): StepSummary[] {
  const groups = new Map<StepCategory, ToolCallData[]>()
  for (const s of steps) {
    if (s.kind !== 'tool') continue
    const c = stepCategory(s.tool.tool)
    groups.set(c, [...(groups.get(c) ?? []), s.tool])
  }
  return [...groups].map(([category, tools]) => {
    if (category === 'read' || category === 'edit') {
      const paths = [...new Set(tools.flatMap(filePaths))]
      if (paths.length === 1) return { category, count: 1, name: baseName(paths[0]) }
      return { category, count: paths.length || tools.length }
    }
    const names = [...new Set(tools.map(t => t.tool))]
    if (category === 'tool' && names.length === 1) return { category, count: tools.length, name: names[0] }
    return { category, count: tools.length }
  })
}

/** What the running step is doing, for the collapsed group line ("Running npm test…"). */
export function runningStep(tool: ToolCallData): { category: StepCategory; detail?: string } {
  const category = stepCategory(tool.tool)
  const path = filePaths(tool)[0]
  switch (category) {
    case 'read': case 'edit': return { category, detail: path ? baseName(path) : undefined }
    case 'command': {
      const cmd = str(tool.input, ['command', 'cmd'])
      return { category, detail: cmd ? stripCdPrefix(cmd).split('\n')[0] : undefined }
    }
    case 'search': return { category, detail: str(tool.input, ['pattern', 'query']) || undefined }
    case 'subagent': return { category, detail: str(tool.input, ['description']) || undefined }
    default: return { category, detail: tool.tool }
  }
}

// Lines shared at the start/end of old and new are context, not changes, so a
// one-line edit inside a large old_string still reads as -1/+1.
function editStats(oldText: string, newText: string): { added: number; removed: number } {
  const a = oldText.split('\n')
  const b = newText.split('\n')
  let start = 0
  while (start < a.length && start < b.length && a[start] === b[start]) start++
  let end = 0
  while (end < a.length - start && end < b.length - start && a[a.length - 1 - end] === b[b.length - 1 - end]) end++
  return { added: b.length - start - end, removed: a.length - start - end }
}

/** Lines added/removed by the file-changing calls (denied or failed calls changed nothing). */
export function diffStats(tools: ToolCallData[]): { added: number; removed: number } {
  let added = 0, removed = 0
  for (const t of tools) {
    if (t.permission === 'denied' || t.error !== undefined) continue
    const input = t.input ?? {}
    if (t.tool === 'edit' && typeof input.old_string === 'string' && typeof input.new_string === 'string') {
      const s = editStats(input.old_string, input.new_string)
      added += s.added
      removed += s.removed
    } else if (t.tool === 'write' && typeof input.content === 'string') {
      added += input.content === '' ? 0 : input.content.split('\n').length
    } else if (t.tool === 'apply-patch' && typeof input.patch === 'string') {
      for (const line of input.patch.split('\n')) {
        if (line.startsWith('+++') || line.startsWith('---')) continue
        if (line.startsWith('+')) added++
        else if (line.startsWith('-')) removed++
      }
    }
  }
  return { added, removed }
}

/** One-line preview of a tool's input: the command, path, query… */
export function stepPreview(input: Record<string, unknown>): string {
  for (const k of ['command', 'file_path', 'path', 'pattern', 'url', 'query', 'question', 'description', 'name', 'title']) {
    const v = input[k]
    if (typeof v === 'string' && v) return (k === 'command' ? stripCdPrefix(v.trim()) : v).replace(/\s+/g, ' ').trim()
  }
  const s = JSON.stringify(input)
  return s === '{}' ? '' : s
}

export function wordCount(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0
}
